/**
 * WE-115: Consent Mode v2 state, shared between the inline bootstrap script
 * (src/utils/head/gtag.tsx, which sets the *default* before GTM loads) and
 * the consent banner (src/components/ClientWrapper/ConsentBanner), which
 * updates it once the user makes a choice.
 *
 * Shown to all users (not geo-targeted to EU) — decided with product, see
 * WE-115-events-specification.md §9.
 */
import { isSSR } from '@/utils/helper';
import { events } from './index';

// Window.dataLayer / Window.gtag are already declared globally in src/auth/types.ts.

export const CONSENT_STORAGE_KEY = 'wizium_consent';
export type ConsentState = 'granted' | 'denied';

const listeners = new Set<() => void>();

/** For React's useSyncExternalStore (ConsentBanner) — hydration-safe read of this store. */
export function subscribeToConsent(callback: () => void): () => void {
    listeners.add(callback);
    return () => listeners.delete(callback);
}

export function getStoredConsent(): ConsentState | null {
    if (isSSR) return null;
    const value = localStorage.getItem(CONSENT_STORAGE_KEY);
    return value === 'granted' || value === 'denied' ? value : null;
}

/** Called by the consent banner when the user accepts/declines tracking. */
export function setConsent(state: ConsentState): void {
    if (isSSR) return;

    const previousState = getStoredConsent();
    localStorage.setItem(CONSENT_STORAGE_KEY, state);
    listeners.forEach(callback => callback());

    window.dataLayer = window.dataLayer || [];
    window.gtag =
        window.gtag ||
        function gtag() {
            window.dataLayer?.push(arguments);
        };
    window.gtag('consent', 'update', {
        ad_storage: state,
        analytics_storage: state,
        ad_user_data: state,
        ad_personalization: state,
    });

    events.emit('consent_updated', {
        analytics_storage: state,
        ad_storage: state,
        previous_state: previousState,
    });
}
